import moment from "moment-timezone";
import { CalendarEvent, PositionedEvent } from "../types";

export interface CalendarLogicParams {
  events: CalendarEvent[];
  startDate: Date;
  numDays: number;
  slotHeight: number;
  timezone: string;
}

export function getDayIndex(date: Date, startDate: Date, timezone: string): number {
  const dateTz = moment.tz(date, timezone).startOf("day");
  const startTz = moment.tz(startDate, timezone).startOf("day");
  return dateTz.diff(startTz, "days");
}

export function calculatePositionedEvents({
  events,
  startDate,
  numDays,
  slotHeight,
  timezone,
}: CalendarLogicParams): PositionedEvent[] {
  const dayBuckets: CalendarEvent[][] = Array.from({ length: numDays }, () => []);

  events.forEach((event) => {
    const dayIndex = getDayIndex(event.start, startDate, timezone);
    if (dayIndex >= 0 && dayIndex < numDays) {
      dayBuckets[dayIndex].push(event);
    }
  });

  const positioned: PositionedEvent[] = [];

  dayBuckets.forEach((dayEvents, dayIndex) => {
    const sorted = [...dayEvents].sort((a, b) => {
      const startDiff = a.start.getTime() - b.start.getTime();
      if (startDiff !== 0) return startDiff;

      // For same start time, prioritize longer events (they should be assigned to leftward columns)
      const aDuration = a.end.getTime() - a.start.getTime();
      const bDuration = b.end.getTime() - b.start.getTime();
      if (aDuration !== bDuration) return bDuration - aDuration;

      // Use event ID as stable tiebreaker to prevent position shifts
      return a.id.localeCompare(b.id);
    });

    const columns: CalendarEvent[][] = [];

    sorted.forEach((event) => {
      const availableCol = columns.find((col) => col[col.length - 1].end <= event.start);

      if (availableCol) {
        availableCol.push(event);
      } else {
        columns.push([event]);
      }
    });

    // Precompute column width factors
    const colCount = columns.length;
    const colPercent = 1 / colCount;
    const availableWidth = 0.9;

    sorted.forEach((event) => {
      const colIndex = columns.findIndex((col) => col.includes(event));

      // Use timezone-aware calculations for positioning
      const startMoment = moment.tz(event.start, timezone);
      const endMoment = moment.tz(event.end, timezone);
      const duration = endMoment.diff(startMoment, "hours", true);
      const top = startMoment.hours() * slotHeight + startMoment.minutes() * (slotHeight / 60);
      const height = duration * slotHeight;

      const left = colIndex * colPercent;
      const width = Math.min(1, colPercent * 2, availableWidth - left);

      positioned.push({
        ...event,
        top,
        height,
        left,
        width,
        dayIndex,
        zIndex: colIndex + 5,
      });
    });
  });

  return positioned;
}

export interface MonthLogicParams {
  events: CalendarEvent[];
  startDate: Date;
  numMonths?: number;
  timezone: string;
}

export function calculateMonthEvents({
  events,
  startDate,
  numMonths = 1,
  timezone,
}: MonthLogicParams): CalendarEvent[] {
  const startMoment = moment.tz(startDate, timezone).startOf("month");
  const endMoment = startMoment.clone().add(numMonths - 1, "months").endOf("month");

  return events.filter((event) => {
    const eventMoment = moment.tz(event.start, timezone);
    return eventMoment.isBetween(startMoment, endMoment, null, "[]");
  }).sort((a, b) => {
    const startDiff = a.start.getTime() - b.start.getTime();
    if (startDiff !== 0) return startDiff;

    // For same start time, prioritize longer events
    const aDuration = a.end.getTime() - a.start.getTime();
    const bDuration = b.end.getTime() - b.start.getTime();
    if (aDuration !== bDuration) return bDuration - aDuration;

    // Use event ID as stable tiebreaker
    return a.id.localeCompare(b.id);
  });
}
